import { ErrorModal, HelmetDexteel, useUserContext } from "@dexteel/mesf-core";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
	INCENTIVE_KPI_NAMES,
	SHIFT_REPORT_CONFIG_INITIAL_VALUES,
	ShiftReportConfig,
	TIME_LEFT_KPI_NAME,
} from "../../models/ShiftReportConfig";
import {
	getShiftReportConfig,
	upsertShiftReportConfig,
} from "../../repositories/ShiftReportConfigRepository";

import { ThresholdSliders } from "./components/ThresholdSliders";
import { TimeThresholdSlider } from "./components/TimeThresholdSlider";

export const useSearchShiftReportConfig = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	return useQuery<ShiftReportConfig, Error>({
		queryKey: ["shiftReportConfig", defaultAreaId],
		queryFn: ({ signal }) => getShiftReportConfig({ defaultAreaId }, signal),
		refetchInterval: 60_000,
	});
};

export const ShiftReportConfigPage = () => {
	const queryClient = useQueryClient();
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const [error, setError] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [config, setConfig] = useState<ShiftReportConfig>(
		SHIFT_REPORT_CONFIG_INITIAL_VALUES,
	);

	const { data, isLoading, error: e, isError } = useSearchShiftReportConfig();

	const saveMutation = useMutation(upsertShiftReportConfig, {
		meta: { successMessage: "Shift Report configuration saved successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shiftReportConfig"] });
		},
		onError: (err: Error) => setError(err.message),
		onSettled: () => setIsSaving(false),
	});

	const handleThresholdChange = (name: string, zone: string, value: number) => {
		setConfig((prev) => ({
			...prev,
			Thresholds: prev.Thresholds.map((t) =>
				t.Name === name ? { ...t, [zone]: value } : t,
			),
		}));
	};

	const kpiThresholds = config.Thresholds.filter(
		(t) =>
			t.Name !== TIME_LEFT_KPI_NAME &&
			!INCENTIVE_KPI_NAMES.includes(
				t.Name as (typeof INCENTIVE_KPI_NAMES)[number],
			),
	);
	const incentiveThresholds = config.Thresholds.filter((t) =>
		INCENTIVE_KPI_NAMES.includes(
			t.Name as (typeof INCENTIVE_KPI_NAMES)[number],
		),
	);
	const timeLeftThresholds = config.Thresholds.filter(
		(t) => t.Name === TIME_LEFT_KPI_NAME,
	);

	const handleSave = () => {
		setIsSaving(true);
		saveMutation.mutate({ defaultAreaId, config });
	};

	useEffect(() => {
		if (data) setConfig(data);
	}, [data]);

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [isError, e]);

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" p={4}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<>
			<HelmetDexteel title="Threshold Adjustments" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "85vh",
					p: 1,
					gap: 2,
				}}
			>
				<Typography variant="h5" fontWeight={600}>
					Threshold Adjustments
				</Typography>

				<Paper sx={{ p: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
						<Typography variant="subtitle1" fontWeight={600}>
							KPI Thresholds (%)
						</Typography>
						<Tooltip
							arrow
							title="Drag the handles to adjust percentage thresholds for each KPI color zone. Values represent the percentage of actual vs. target."
						>
							<InfoOutlinedIcon
								sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
							/>
						</Tooltip>
					</Box>
					<ThresholdSliders
						thresholds={kpiThresholds}
						onChange={handleThresholdChange}
					/>

					{incentiveThresholds.length > 0 && (
						<>
							<Divider sx={{ my: 2 }} />

							<Box
								sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}
							>
								<Typography variant="subtitle1" fontWeight={600}>
									Incentive Thresholds (%)
								</Typography>
								<Tooltip
									arrow
									title="Drag the handles to adjust percentage thresholds for each Incentive color zone."
								>
									<InfoOutlinedIcon
										sx={{
											fontSize: 16,
											color: "text.secondary",
											cursor: "help",
										}}
									/>
								</Tooltip>
							</Box>
							<ThresholdSliders
								thresholds={incentiveThresholds}
								onChange={handleThresholdChange}
							/>
						</>
					)}

					<Divider sx={{ my: 2 }} />

					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
						<Typography variant="subtitle1" fontWeight={600}>
							Time Left Thresholds (hours)
						</Typography>
						<Tooltip
							arrow
							title='Drag the handles to adjust each zone boundary. Set "Max Hours" to change the scale endpoint.'
						>
							<InfoOutlinedIcon
								sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
							/>
						</Tooltip>
					</Box>
					<TimeThresholdSlider
						thresholds={timeLeftThresholds}
						onChange={handleThresholdChange}
					/>
				</Paper>

				<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
					<Button
						variant="contained"
						size="small"
						startIcon={
							isSaving ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<SaveIcon />
							)
						}
						onClick={handleSave}
						disabled={isSaving}
					>
						Save
					</Button>
				</Box>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
